import { FormEvent, useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type Assignment = Awaited<ReturnType<typeof api.userRoleAssignments>>["assignments"][number];

const PLATFORM_ROLES = [
  "PLATFORM_SUPER_ADMIN",
  "PLATFORM_OPERATIONS_ADMIN",
  "PLATFORM_BILLING_ADMIN",
  "PLATFORM_SECURITY_ADMIN",
  "PLATFORM_SUPPORT_ADMIN",
  "PLATFORM_PRODUCT_OPS_ADMIN",
  "PLATFORM_AUDITOR"
] as const;

const WORKSPACE_ROLES = [
  "WORKSPACE_OWNER",
  "ORGANIZATION_ADMIN",
  "BILLING_ADMIN",
  "DHIS2_INTEGRATION_ADMIN",
  "SECURITY_ADMIN",
  "ANALYTICS_ADMIN",
  "FORWARDING_ADMIN",
  "PORTAL_PUBLISHER",
  "AUDITOR",
  "READ_ONLY_USER"
] as const;

export default function AdminUserRoles() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<"active" | "revoked" | "all">("active");

  const [userId, setUserId] = useState("");
  const [roleKey, setRoleKey] = useState<string>(PLATFORM_ROLES[0]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);

  const isWorkspaceRole = (WORKSPACE_ROLES as readonly string[]).includes(roleKey);

  const tAssignments = useTableState(assignments as unknown as Array<Record<string, unknown>>, {
    searchFields: ["userId", "roleKey", "scope", "workspaceId", "status"],
    initialPageSize: 25
  });

  const load = () => {
    setLoading(true);
    setErr(null);
    api.userRoleAssignments(filter === "all" ? {} : { status: filter })
      .then((d) => setAssignments(d.assignments))
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const onGrant = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      await api.grantUserRole({
        userId: userId.trim(),
        roleKey,
        workspaceId: isWorkspaceRole ? workspaceId.trim() || undefined : undefined,
        expiresAt: expiresAt || undefined
      });
      setUserId("");
      setWorkspaceId("");
      setExpiresAt("");
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const onRevoke = async (id: string) => {
    if (!confirm("Revoke this role assignment?")) return;
    setErr(null);
    try {
      await api.revokeUserRole(id);
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>User role assignments</h1>
          <p>Persisted, multi-role-per-user grants that layer on top of legacy tenant-member roles. Revocation is audited.</p>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}

      <form className="card" onSubmit={onGrant} style={{ marginBottom: 16 }}>
        <div className="card-head"><strong>Grant a role</strong></div>
        <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          <div className="field">
            <label>User id</label>
            <input type="text" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="mem-..." required />
          </div>
          <div className="field">
            <label>Role</label>
            <select value={roleKey} onChange={(e) => setRoleKey(e.target.value)}>
              <optgroup label="Platform">
                {PLATFORM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </optgroup>
              <optgroup label="Workspace">
                {WORKSPACE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </optgroup>
            </select>
          </div>
          <div className="field">
            <label>Workspace id {isWorkspaceRole ? "" : "(n/a)"}</label>
            <input
              type="text"
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              placeholder={isWorkspaceRole ? "ws-..." : "platform scope"}
              disabled={!isWorkspaceRole}
            />
          </div>
          <div className="field">
            <label>Expires at (optional)</label>
            <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
        </div>
        <div className="card-foot" style={{ display: "flex", gap: 8, padding: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Granting…" : "Grant role"}
          </button>
        </div>
      </form>

      <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <span className="soft">Filter:</span>
        {(["active", "revoked", "all"] as const).map((f) => (
          <button
            key={f}
            type="button"
            className={`btn btn-small ${filter === f ? "btn-primary" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       assignments.length === 0 ? (
         <EmptyState
           icon="🔐"
           title="No role assignments match"
           description="Adjust the filters above, or grant a new platform / workspace role from the Roles & permissions page."
         />
       ) : (
        <div>
          <TableToolbar
            search={tAssignments.search} setSearch={tAssignments.setSearch}
            page={tAssignments.page} setPage={tAssignments.setPage}
            pageSize={tAssignments.pageSize} setPageSize={tAssignments.setPageSize}
            totalPages={tAssignments.totalPages} totalRows={tAssignments.totalRows}
            hideExport
            searchPlaceholder="Search user / role / workspace…"
          />
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>User</th><th>Role</th><th>Scope</th><th>Workspace</th><th>Status</th><th>Granted</th><th>Expires</th><th></th></tr>
            </thead>
            <tbody>
              {(tAssignments.paged as unknown as Assignment[]).map((a) => (
                <tr key={a.id}>
                  <td><code>{a.userId}</code></td>
                  <td><code>{a.roleKey}</code></td>
                  <td>{a.scope}</td>
                  <td className="soft">{a.workspaceId ? <code>{a.workspaceId}</code> : "—"}</td>
                  <td>
                    <span className={`pill ${a.status === "active" ? "pill-success" : "pill-muted"}`}>{a.status}</span>
                  </td>
                  <td className="soft">{new Date(a.assignedAt).toLocaleString()}</td>
                  <td className="soft">{a.expiresAt ? new Date(a.expiresAt).toLocaleString() : "—"}</td>
                  <td>
                    {a.status === "active" && (
                      <button type="button" className="btn btn-small" onClick={() => onRevoke(a.id)}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
       )}
    </div>
  );
}
